export const ITEM_STATES = Object.freeze({
  DECLARED: "declared",
  RECEIVED: "received",
  PARSED: "parsed",
  RENDERABLE: "renderable",
  FAILED: "failed"
});

const ITEM_STATE_RANK = Object.freeze({
  [ITEM_STATES.DECLARED]: 0,
  [ITEM_STATES.RECEIVED]: 1,
  [ITEM_STATES.PARSED]: 2,
  [ITEM_STATES.RENDERABLE]: 3,
  [ITEM_STATES.FAILED]: 4
});

/**
 * Normalizes tracker item ids to stable string keys.
 * @param {string|number} itemId
 * @returns {string}
 */
const normalizeItemId = itemId => String(itemId);

/**
 * Ensures tracker contains an entry for the given item id.
 * @param {Object} tracker
 * @param {string|number} itemId
 * @returns {Object|null}
 */
const ensureTrackedItem = (tracker, itemId) => {
  if (!tracker) return null;
  const key = normalizeItemId(itemId);
  if (!tracker.items[key]) {
    tracker.items[key] = {
      id: itemId,
      state: ITEM_STATES.DECLARED
    };
    tracker.declaredOrder.push(key);
  }
  return tracker.items[key];
};

/**
 * Counts tracker items grouped by logical state.
 * @param {Object} tracker
 * @returns {Object}
 */
const countByState = tracker => {
  const counts = {
    [ITEM_STATES.DECLARED]: 0,
    [ITEM_STATES.RECEIVED]: 0,
    [ITEM_STATES.PARSED]: 0,
    [ITEM_STATES.RENDERABLE]: 0,
    [ITEM_STATES.FAILED]: 0
  };
  Object.values(tracker?.items || {}).forEach(item => {
    const state = item?.state;
    if (state && Object.prototype.hasOwnProperty.call(counts, state)) {
      counts[state] += 1;
    }
  });
  return counts;
};

/**
 * Builds per-series download tracker initialized with declared image ids.
 * @param {Array<string|number>} imageIds
 * @returns {Object}
 */
export const createSeriesDownloadTracker = imageIds => {
  const tracker = {
    items: {},
    declaredOrder: [],
    inFlight: new Set(),
    parseQueued: new Set(),
    completed: false
  };
  (imageIds || []).forEach(itemId => {
    ensureTrackedItem(tracker, itemId);
  });
  return tracker;
};

/**
 * Transitions one tracked item state without allowing backward transitions.
 * @param {Object} tracker
 * @param {string|number} itemId
 * @param {string} nextState
 * @returns {string|null}
 */
export const transitionSeriesItemState = (tracker, itemId, nextState) => {
  if (!tracker || !nextState || !Object.prototype.hasOwnProperty.call(ITEM_STATE_RANK, nextState)) {
    return null;
  }
  const item = ensureTrackedItem(tracker, itemId);
  if (!item) return null;
  const currentRank = ITEM_STATE_RANK[item.state];
  const nextRank = ITEM_STATE_RANK[nextState];
  if (Number.isFinite(currentRank) && currentRank > nextRank) {
    return item.state;
  }
  item.state = nextState;
  return item.state;
};

/**
 * Sets or unsets in-flight flag for one series item.
 * @param {Object} tracker
 * @param {string|number} itemId
 * @param {boolean} [value=true]
 * @returns {void}
 */
export const setSeriesItemInFlight = (tracker, itemId, value = true) => {
  if (!tracker) return;
  const key = normalizeItemId(itemId);
  ensureTrackedItem(tracker, itemId);
  if (value) {
    tracker.inFlight.add(key);
    return;
  }
  tracker.inFlight.delete(key);
};

/**
 * Sets or unsets parse-queued flag for one series item.
 * @param {Object} tracker
 * @param {string|number} itemId
 * @param {boolean} [value=true]
 * @returns {void}
 */
export const setSeriesItemParseQueued = (tracker, itemId, value = true) => {
  if (!tracker) return;
  const key = normalizeItemId(itemId);
  ensureTrackedItem(tracker, itemId);
  if (value) {
    tracker.parseQueued.add(key);
    return;
  }
  tracker.parseQueued.delete(key);
};

/**
 * Returns an aggregate tracker snapshot used by completion checks.
 * @param {Object} tracker
 * @param {Object} [extra]
 * @returns {Object}
 */
export const getSeriesTrackerSnapshot = (tracker, extra = {}) => {
  const counts = countByState(tracker || {});
  const declaredOrder = tracker?.declaredOrder || [];
  const declaredCount = declaredOrder.length;
  const inFlightIds = tracker ? Array.from(tracker.inFlight) : [];
  const parseQueuedIds = tracker ? Array.from(tracker.parseQueued) : [];
  const lastDeclaredKey = declaredOrder.length
    ? declaredOrder[declaredOrder.length - 1]
    : null;
  const lastDeclared = lastDeclaredKey && tracker?.items
    ? tracker.items[lastDeclaredKey]
    : null;
  const unresolvedIds = declaredOrder.filter(itemKey => {
    const state = tracker?.items?.[itemKey]?.state;
    return state !== ITEM_STATES.RENDERABLE && state !== ITEM_STATES.FAILED;
  });

  return {
    declaredCount,
    stateCounts: counts,
    renderableCount: counts[ITEM_STATES.RENDERABLE],
    failedCount: counts[ITEM_STATES.FAILED],
    inFlightIds,
    parseQueuedIds,
    unresolvedIds,
    lastDeclaredItemId: lastDeclared ? lastDeclared.id : null,
    lastDeclaredItemState: lastDeclared ? lastDeclared.state : null,
    pendingChunkCount: Number.isFinite(extra.pendingChunkCount)
      ? extra.pendingChunkCount
      : 0,
    parseQueueLength: Number.isFinite(extra.parseQueueLength)
      ? extra.parseQueueLength
      : 0
  };
};

/**
 * Returns completion gate result with blocker reason and snapshot.
 * @param {Object} tracker
 * @param {Object} [extra]
 * @returns {{canFinalize:boolean,reason:string,snapshot:Object}}
 */
export const canFinalizeSeriesDownload = (tracker, extra = {}) => {
  const snapshot = getSeriesTrackerSnapshot(tracker, extra);
  const {
    declaredCount,
    renderableCount,
    failedCount,
    inFlightIds,
    parseQueuedIds,
    pendingChunkCount,
    parseQueueLength,
    unresolvedIds
  } = snapshot;

  if (declaredCount === 0) {
    return { canFinalize: true, reason: "no_declared_items", snapshot };
  }
  if (renderableCount + failedCount < declaredCount || unresolvedIds.length > 0) {
    return { canFinalize: false, reason: "items_not_renderable", snapshot };
  }
  if (inFlightIds.length > 0) {
    return { canFinalize: false, reason: "items_in_flight", snapshot };
  }
  if (parseQueuedIds.length > 0) {
    return { canFinalize: false, reason: "items_waiting_parse", snapshot };
  }
  if (pendingChunkCount > 0) {
    return { canFinalize: false, reason: "chunks_pending", snapshot };
  }
  if (parseQueueLength > 0) {
    return { canFinalize: false, reason: "parse_queue_backlog", snapshot };
  }

  if (failedCount > 0) {
    return { canFinalize: true, reason: "items_failed", snapshot };
  }

  return { canFinalize: true, reason: "all_items_renderable", snapshot };
};
